import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Beratungstermin, BeratungsArt, Berater } from '../types';
import { beratungService, beraterService } from '../services/beratungService';
import { useAuth } from '../contexts/AuthContext';
import { Icon, PageHeader, LoadingScreen, EmptyState, FilterStand, FilterZuruecksetzen, tableCard, th, td, tableRow, fehlerText } from '../components/ui';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { useSearchParams } from 'react-router-dom';

const formatDateTime = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const artLabel = (art: BeratungsArt) =>
  art === 'NEUBAU' ? 'Neubau' : art === 'SANIERUNG' ? 'Sanierung' : 'Service für Bestand';
const artColor = (art: BeratungsArt) =>
  art === 'NEUBAU'
    ? 'bg-blue-100 text-blue-800'
    : art === 'SANIERUNG'
    ? 'bg-purple-100 text-purple-800'
    : 'bg-amber-100 text-amber-800';

// Sonderwert für „kein Berater zugeordnet" in der Filterleiste
const OHNE_BERATER = -1;

const Beratungstermine: React.FC = () => {
  const { isAdmin } = useAuth();
  const [termine, setTermine] = useState<Beratungstermin[]>([]);
  const [berater, setBerater] = useState<Berater[]>([]);
  const [loading, setLoading] = useState(true);
  // `?filter=alle` kommt aus der Suche, wenn ein erledigter Termin angesprungen wird.
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<'offen' | 'alle'>(() => (searchParams.get('filter') === 'alle' ? 'alle' : 'offen'));
  const [beraterFilter, setBeraterFilter] = useState<number | 'alle'>('alle');
  const [showBeraterModal, setShowBeraterModal] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [t, b] = await Promise.all([beratungService.getAll(), beraterService.getAll()]);
      setTermine(t);
      setBerater(b);
    } catch (err) {
      toast.error(fehlerText(err, 'Beratungstermine konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const toggleErledigt = async (t: Beratungstermin) => {
    try {
      const updated = await beratungService.setErledigt(t.id, !t.erledigt);
      markiereNeu(`termin-${t.id}`);
      setTermine((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      toast.success(updated.erledigt ? 'Anfrage erledigt' : 'Anfrage wieder geöffnet');
    } catch (err) {
      toast.error(fehlerText(err, 'Anfrage konnte nicht gespeichert werden'));
    }
  };

  const handleDelete = async (t: Beratungstermin) => {
    if (!window.confirm(`Anfrage „${t.vorname ? t.vorname + ' ' : ''}${t.nachname}“ löschen?`)) return;
    try {
      await beratungService.delete(t.id);
      setTermine((prev) => prev.filter((x) => x.id !== t.id));
      toast.success('Anfrage gelöscht');
    } catch (err) {
      toast.error(fehlerText(err, 'Anfrage konnte nicht gelöscht werden'));
    }
  };

  // Auswertung: Anzahl Termine je Berater (inkl. inaktiver, solange noch Termine vorhanden)
  const beraterCounts = useMemo(() => {
    const map = new Map<number, { name: string; count: number }>();
    let ohne = 0;
    for (const t of termine) {
      if (t.beraterId) {
        const entry = map.get(t.beraterId);
        if (entry) entry.count++;
        else map.set(t.beraterId, { name: t.beraterName || 'Unbekannt', count: 1 });
      } else {
        ohne++;
      }
    }
    const list = Array.from(map.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.count - a.count);
    return { list, ohne };
  }, [termine]);

  const sichtbar = termine.filter((t) => {
    if (filter === 'offen' && t.erledigt) return false;
    if (beraterFilter === 'alle') return true;
    if (beraterFilter === OHNE_BERATER) return !t.beraterId;
    return t.beraterId === beraterFilter;
  });
  const offen = termine.filter((t) => !t.erledigt).length;

  const setBeraterChip = (value: number | 'alle') =>
    setBeraterFilter((prev) => (prev === value ? 'alle' : value));
  const filterZuruecksetzen = () => { setBeraterFilter('alle'); setFilter('offen'); };
  const statusWechsel = (t: Beratungstermin) => {
    const a = ankunft(`termin-${t.id}`);
    return a.className ? 'ui-wechsel' : '';
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Beratungstermine"
        subtitle={`Anfragen vom Event-Formular · ${offen} offen`}
        actions={<>
          {isAdmin() && (
            <button
              onClick={() => setShowBeraterModal(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Berater verwalten
            </button>
          )}
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setFilter('offen')}
              className={`px-4 py-2 text-sm font-medium ${filter === 'offen' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Offen
            </button>
            <button
              onClick={() => setFilter('alle')}
              className={`px-4 py-2 text-sm font-medium ${filter === 'alle' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Alle
            </button>
          </div>
        </>}
      />

      {/* Auswertung pro Berater */}
      {!loading && (beraterCounts.list.length > 0 || beraterCounts.ohne > 0) && (
        <div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-gray-700">Termine je Berater</div>
            <FilterStand
              treffer={sichtbar.length}
              gesamt={filter === 'offen' ? offen : termine.length}
              einheit={filter === 'offen' ? 'offene Anfragen' : 'Anfragen'}
              aktiv={beraterFilter !== 'alle'}
              onZuruecksetzen={() => setBeraterFilter('alle')}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {beraterCounts.list.map((b) => {
              const active = beraterFilter === b.id;
              return (
                <button
                  key={b.id}
                  onClick={() => setBeraterChip(b.id)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    active ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-300 hover:border-brand-400'
                  }`}
                >
                  <span className="font-medium">{b.name}</span>
                  <span className={`inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-xs font-semibold ${
                    active ? 'bg-white/25 text-white' : 'bg-brand-100 text-brand-800'
                  }`}>
                    {b.count}
                  </span>
                </button>
              );
            })}
            {beraterCounts.ohne > 0 && (
              <button
                onClick={() => setBeraterChip(OHNE_BERATER)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  beraterFilter === OHNE_BERATER ? 'bg-gray-600 text-white border-gray-600' : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
                }`}
              >
                <span>Ohne Berater</span>
                <span className={`inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-xs font-semibold ${
                  beraterFilter === OHNE_BERATER ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-700'
                }`}>
                  {beraterCounts.ohne}
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <LoadingScreen />
      ) : sichtbar.length === 0 ? (
        beraterFilter !== 'alle'
          ? <EmptyState icon={<Icon.Search />} title="Keine Anfragen für diesen Filter" action={<FilterZuruecksetzen onClick={filterZuruecksetzen} />} />
          : filter === 'offen'
            ? <EmptyState icon={<Icon.Check />} title="Keine offenen Anfragen" hint="Alle Anfragen sind erledigt." action={termine.length > 0 ? <button type="button" onClick={() => setFilter('alle')} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">Alle Anfragen zeigen</button> : undefined} />
            : <EmptyState icon={<Icon.Kalender />} title="Noch keine Anfragen eingegangen" hint="Anfragen kommen über das Event-Formular (/event) herein." />
      ) : (
        <>
          {/* Desktop-Tabelle */}
          <div className={`hidden md:block ${tableCard}`}>
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Kontakt</th>
                  <th className={th}>PLZ</th>
                  <th className={th}>Beratung</th>
                  <th className={th}>Berater</th>
                  <th className={th}>Eingegangen</th>
                  <th className={`${th} text-right`}><span className="sr-only">Aktionen</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sichtbar.map((t) => {
                  const a = ankunft(`termin-${t.id}`);
                  return (
                  <tr key={t.id} ref={a.ref} className={`${tableRow} ${t.erledigt ? 'bg-gray-50 text-gray-400' : ''} ${a.className}`}>
                    <td className={`${td} font-semibold align-top`}>
                      {t.vorname ? `${t.vorname} ` : ''}{t.nachname}
                      {t.bemerkung && (
                        <div className="mt-1 text-xs font-normal text-gray-500 whitespace-pre-wrap max-w-xs">{t.bemerkung}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm align-top">
                      <a href={`tel:${t.telefon}`} className="text-brand-600 hover:underline">{t.telefon}</a>
                      {t.email && (
                        <>
                          <br />
                          <a href={`mailto:${t.email}`} className="text-brand-600 hover:underline">{t.email}</a>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm align-top">{t.plz}</td>
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${artColor(t.beratungsart)}`}>
                        {artLabel(t.beratungsart)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm align-top">{t.beraterName}</td>
                    <td className="px-4 py-3 text-sm align-top">{formatDateTime(t.erstelltAm)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap align-top">
                      <button
                        key={String(t.erledigt)}
                        onClick={() => toggleErledigt(t)}
                        className={`text-sm text-brand-600 hover:underline mr-4 ${statusWechsel(t)}`}
                      >
                        {t.erledigt ? 'Wieder öffnen' : 'Erledigt'}
                      </button>
                      <button
                        onClick={() => handleDelete(t)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile-Karten */}
          <div className="md:hidden space-y-3">
            {sichtbar.map((t) => {
              const a = ankunft(`termin-${t.id}`);
              return (
              <div key={t.id} ref={a.ref} className={`bg-white rounded-lg shadow p-4 ${t.erledigt ? 'opacity-60' : ''} ${a.className}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-gray-900">
                    {t.vorname ? `${t.vorname} ` : ''}{t.nachname}
                  </div>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${artColor(t.beratungsart)}`}>
                    {artLabel(t.beratungsart)}
                  </span>
                </div>
                <div className="mt-2 text-sm space-y-1">
                  <div><a href={`tel:${t.telefon}`} className="text-brand-600">{t.telefon}</a></div>
                  {t.email && <div><a href={`mailto:${t.email}`} className="text-brand-600 break-all">{t.email}</a></div>}
                  <div className="text-gray-500">PLZ {t.plz} · {formatDateTime(t.erstelltAm)}</div>
                  {t.beraterName && <div className="text-gray-700">Berater: <span className="font-medium">{t.beraterName}</span></div>}
                  {t.bemerkung && <div className="text-gray-600 whitespace-pre-wrap">{t.bemerkung}</div>}
                </div>
                <div className="mt-3 flex gap-4 border-t pt-3">
                  <button onClick={() => toggleErledigt(t)} className="text-sm text-brand-600">
                    {t.erledigt ? 'Wieder öffnen' : 'Erledigt'}
                  </button>
                  <button onClick={() => handleDelete(t)} className="text-sm text-red-600">Löschen</button>
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}

      {showBeraterModal && (
        <BeraterModal
          berater={berater}
          onClose={() => setShowBeraterModal(false)}
          onChanged={load}
        />
      )}
    </div>
  );
};

// --- Berater-Verwaltung (Admin) ---
interface BeraterModalProps {
  berater: Berater[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}

const BeraterModal: React.FC<BeraterModalProps> = ({ berater, onClose, onChanged }) => {
  const [neuName, setNeuName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [busy, setBusy] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = neuName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await beraterService.create({ name });
      setNeuName('');
      await onChanged();
      toast.success('Berater angelegt');
    } catch (err) {
      toast.error(fehlerText(err, 'Berater konnte nicht angelegt werden'));
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (id: number) => {
    const name = editingName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await beraterService.update(id, { name });
      setEditingId(null);
      await onChanged();
      toast.success('Berater gespeichert');
    } catch (err) {
      toast.error(fehlerText(err, 'Berater konnte nicht gespeichert werden'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (b: Berater) => {
    if (!window.confirm(`Berater „${b.name}“ löschen?`)) return;
    setBusy(true);
    try {
      await beraterService.delete(b.id);
      await onChanged();
      toast.success('Berater gelöscht');
    } catch (err) {
      toast.error(fehlerText(err, 'Berater konnte nicht gelöscht werden'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="ui-pop bg-white rounded-lg w-full max-w-md flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold">Berater verwalten</h2>
          <button onClick={onClose} aria-label="Schließen" className="text-gray-400 hover:text-gray-600"><Icon.Close className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleCreate} className="flex gap-2 px-5 py-4 border-b">
          <input
            type="text"
            value={neuName}
            onChange={(e) => setNeuName(e.target.value)}
            placeholder="Name des Beraters"
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
            disabled={busy}
          />
          <button
            type="submit"
            disabled={busy || !neuName.trim()}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm disabled:opacity-50"
          >
            Berater anlegen
          </button>
        </form>

        <div className="overflow-y-auto px-5 py-3 flex-1">
          {berater.length === 0 ? (
            <EmptyState title="Noch keine Berater" hint="Legen Sie oben den ersten Berater an." className="!py-8" />
          ) : (
            <ul className="divide-y divide-gray-100">
              {berater.map((b) => (
                <li key={b.id} className="flex items-center gap-2 py-2">
                  {editingId === b.id ? (
                    <>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 p-1.5 border border-gray-300 rounded text-sm"
                        autoFocus
                        disabled={busy}
                      />
                      <button onClick={() => handleRename(b.id)} disabled={busy} className="text-sm text-brand-600 hover:underline">Speichern</button>
                      <button onClick={() => setEditingId(null)} className="text-sm text-gray-500 hover:underline">Abbrechen</button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-800">{b.name}</span>
                      <button
                        onClick={() => { setEditingId(b.id); setEditingName(b.name); }}
                        className="text-sm text-brand-600 hover:underline"
                      >
                        Bearbeiten
                      </button>
                      <button onClick={() => handleDelete(b)} disabled={busy} className="text-sm text-red-600 hover:underline">Löschen</button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t text-right">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default Beratungstermine;
